import { supabase } from "./supabase";
import type { HelpArticle } from "./types";

export interface AdminArticle extends HelpArticle {}

export async function fetchAllArticlesForAdmin(): Promise<HelpArticle[]> {
  const { data, error } = await supabase
    .from("help_articles")
    .select("*")
    .order("nav_order", { ascending: true });
  if (error) throw new Error(error.message);
  return data as HelpArticle[];
}

export async function fetchArticleById(id: string): Promise<HelpArticle | null> {
  const { data, error } = await supabase
    .from("help_articles")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as HelpArticle | null;
}

export async function checkSlugUnique(slug: string, excludeId?: string): Promise<boolean> {
  let q = supabase.from("help_articles").select("id").eq("slug", slug);
  if (excludeId) q = q.neq("id", excludeId);
  const { data, error } = await q.maybeSingle();
  if (error) throw new Error(error.message);
  return !data;
}

export async function checkRedirectExists(oldSlug: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("help_redirects")
    .select("old_slug")
    .eq("old_slug", oldSlug)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return !!data;
}

interface ArticleInput {
  title: string;
  description: string;
  content_markdown: string;
  parent_slug: string | null;
  nav_order: number;
  page_type: string;
  is_published: boolean;
  slug?: string;
}

export async function createArticle(input: ArticleInput & { slug: string }): Promise<HelpArticle> {
  const { data, error } = await supabase
    .from("help_articles")
    .insert({
      slug: input.slug,
      title: input.title,
      description: input.description,
      content_markdown: input.content_markdown,
      parent_slug: input.parent_slug,
      nav_order: input.nav_order,
      page_type: input.page_type,
      is_published: input.is_published,
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data as HelpArticle;
}

export async function updateArticle(
  id: string,
  input: ArticleInput,
  oldSlug?: string,
  newSlug?: string
): Promise<HelpArticle> {
  const update: Record<string, unknown> = {
    title: input.title,
    description: input.description,
    content_markdown: input.content_markdown,
    parent_slug: input.parent_slug,
    nav_order: input.nav_order,
    page_type: input.page_type,
    is_published: input.is_published,
  };

  if (newSlug && oldSlug && newSlug !== oldSlug) {
    update.slug = newSlug;
  }

  const { data, error } = await supabase
    .from("help_articles")
    .update(update)
    .eq("id", id)
    .select("*")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Article not found or access denied");

  if (newSlug && oldSlug && newSlug !== oldSlug) {
    const redirectExists = await checkRedirectExists(oldSlug);
    if (!redirectExists) {
      await supabase.from("help_redirects").insert({
        old_slug: oldSlug,
        new_slug: newSlug,
        http_status: 301,
      });
    }
  }

  return data as HelpArticle;
}

export async function archiveArticle(id: string): Promise<void> {
  const { error } = await supabase
    .from("help_articles")
    .update({ archived_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

export async function restoreArticle(id: string): Promise<void> {
  const { error } = await supabase
    .from("help_articles")
    .update({ archived_at: null })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

export async function fetchRevisions(articleId: string): Promise<HelpArticle[]> {
  const { data, error } = await supabase
    .from("help_article_revisions")
    .select("*")
    .eq("article_id", articleId)
    .order("changed_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data || []) as unknown as HelpArticle[];
}

export async function restoreRevision(articleId: string, revisionId: number): Promise<HelpArticle> {
  const { data: rev, error: revError } = await supabase
    .from("help_article_revisions")
    .select("*")
    .eq("article_id", articleId)
    .eq("id", revisionId)
    .maybeSingle();
  if (revError) throw new Error(revError.message);
  if (!rev) throw new Error("Revision not found");

  const { data, error } = await supabase
    .from("help_articles")
    .update({
      slug: rev.slug,
      title: rev.title,
      description: rev.description,
      content_markdown: rev.content_markdown,
      parent_slug: rev.parent_slug,
      nav_order: rev.nav_order,
      page_type: rev.page_type,
      is_published: rev.is_published,
      archived_at: rev.archived_at,
    })
    .eq("id", articleId)
    .select("*")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Article not found or access denied");
  return data as HelpArticle;
}

export async function uploadImage(file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "png";
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${ext}`;
  const path = `uploads/${filename}`;

  const { error } = await supabase.storage
    .from("help-assets")
    .upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
  if (error) throw new Error(error.message);

  const { data } = supabase.storage.from("help-assets").getPublicUrl(path);
  return data.publicUrl;
}
